import { Modal, TextInput, Button, Stack, Group, Select } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { categorySchema, type CategoryFormValues } from '../schemas/categorySchema';
import type { Category } from '@/types/models';

interface CategoryFormProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: CategoryFormValues, keepOpen?: boolean) => void;
  category?: Category | null;
  isLoading?: boolean;
  /** When true, hides "Create & Add Another" button (for inline use in asset form) */
  inlineMode?: boolean;
}

export function CategoryForm({ opened, onClose, onSubmit, category, isLoading, inlineMode = false }: CategoryFormProps) {
  const form = useForm<CategoryFormValues>({
    validate: zodResolver(categorySchema),
    initialValues: {
      name: '',
      icon: null,
      status: 'active',
    },
  });

  useEffect(() => {
    if (category) {
      form.setValues({
        name: category.name,
        icon: category.icon,
        status: category.status || 'active',
      });
    } else {
      form.reset();
    }
  }, [category, opened]);

  const handleSubmit = (values: CategoryFormValues, keepOpen = false) => {
    onSubmit(values, keepOpen);
    if (keepOpen) {
      form.reset();
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={category ? 'Edit Category' : 'Create Category'}
      size="md"
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        onKeyDown={(e) => {
          // Allow Enter to submit form from any field (except textareas)
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
            e.preventDefault();
            form.onSubmit(handleSubmit)();
          }
        }}
      >
        <Stack gap="md">
          <TextInput
            label="Category Name"
            placeholder="e.g., Laptops, Monitors, Furniture"
            required
            {...form.getInputProps('name')}
          />

          <Select
            label="Icon"
            placeholder="Select an emoji or type your own"
            description="Choose a preset emoji or paste/type any emoji"
            searchable
            clearable
            maxLength={4}
            data={[
              { value: '💻', label: '💻 Laptop' },
              { value: '🖥️', label: '🖥️ Desktop Computer' },
              { value: '⌨️', label: '⌨️ Keyboard' },
              { value: '🖱️', label: '🖱️ Mouse' },
              { value: '🖨️', label: '🖨️ Printer' },
              { value: '📱', label: '📱 Mobile Phone' },
              { value: '📞', label: '📞 Telephone' },
              { value: '📟', label: '📟 Pager' },
              { value: '📠', label: '📠 Fax Machine' },
              { value: '📺', label: '📺 Monitor/TV' },
              { value: '📷', label: '📷 Camera' },
              { value: '📹', label: '📹 Video Camera' },
              { value: '🎥', label: '🎥 Movie Camera' },
              { value: '📡', label: '📡 Network Equipment' },
              { value: '🔌', label: '🔌 Electronics' },
              { value: '🔋', label: '🔋 Battery/UPS' },
              { value: '💡', label: '💡 Lighting' },
              { value: '🪑', label: '🪑 Chair' },
              { value: '🛋️', label: '🛋️ Couch' },
              { value: '🗄️', label: '🗄️ File Cabinet' },
              { value: '📚', label: '📚 Books/Library' },
              { value: '🗂️', label: '🗂️ Filing' },
              { value: '📋', label: '📋 Clipboard/Office' },
              { value: '🖼️', label: '🖼️ Art/Decoration' },
              { value: '🚗', label: '🚗 Vehicle' },
              { value: '🚙', label: '🚙 Car' },
              { value: '🚚', label: '🚚 Truck' },
              { value: '🚐', label: '🚐 Van' },
              { value: '🏍️', label: '🏍️ Motorcycle' },
              { value: '🚲', label: '🚲 Bicycle' },
              { value: '🛠️', label: '🛠️ Tools' },
              { value: '🔧', label: '🔧 Wrench/Tool' },
              { value: '🔨', label: '🔨 Hammer' },
              { value: '⚙️', label: '⚙️ Machinery' },
              { value: '🏭', label: '🏭 Factory Equipment' },
              { value: '🔬', label: '🔬 Lab Equipment' },
              { value: '⚗️', label: '⚗️ Laboratory' },
              { value: '🩺', label: '🩺 Medical Equipment' },
              { value: '💊', label: '💊 Pharmaceutical' },
              { value: '🎵', label: '🎵 Audio Equipment' },
              { value: '🎸', label: '🎸 Musical Instruments' },
              { value: '🎮', label: '🎮 Gaming/Entertainment' },
              { value: '📦', label: '📦 Storage/Package' },
              { value: '🏢', label: '🏢 Building/Real Estate' },
              { value: '🔐', label: '🔐 Security' },
              { value: '🔑', label: '🔑 Keys/Access' },
              { value: '📊', label: '📊 Analytics/Data' },
              { value: '💼', label: '💼 Business Equipment' },
              { value: '🎨', label: '🎨 Design/Creative' },
              { value: '✏️', label: '✏️ Stationery' },
              { value: '📐', label: '📐 Drafting/Engineering' },
              { value: '🧰', label: '🧰 Toolbox/Kit' },
              { value: '🧪', label: '🧪 Science Equipment' },
              { value: '🎯', label: '🎯 Sports Equipment' },
              { value: '🏋️', label: '🏋️ Fitness Equipment' },
              { value: '🍽️', label: '🍽️ Kitchen/Dining' },
              { value: '❄️', label: '❄️ Refrigeration/HVAC' },
              { value: '🌡️', label: '🌡️ Climate Control' },
              { value: '💾', label: '💾 Storage Media' },
              { value: '🖲️', label: '🖲️ Trackball/Input' },
              { value: '🎛️', label: '🎛️ Control Panel' },
              { value: '🗃️', label: '🗃️ Archive/Storage' },
            ]}
            {...form.getInputProps('icon')}
          />

          {!inlineMode && (
            <Select
              label="Status"
              placeholder="Select status"
              data={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              {...form.getInputProps('status')}
            />
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            {!category && !inlineMode && (
              <Button
                type="button"
                variant="light"
                onClick={() => form.onSubmit((values) => handleSubmit(values, true))()}
                loading={isLoading}
              >
                Create & Add Another
              </Button>
            )}
            <Button
              type="button"
              onClick={() => form.onSubmit((values) => handleSubmit(values, false))()}
              loading={isLoading}
            >
              {category ? 'Update Category' : 'Create Category'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
